import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ClipboardCheck, Gavel } from 'lucide-react'
import { Badge, Sheet } from '../components/ui.tsx'
import { get } from '../lib/api.ts'
import { fmtDate } from '../lib/format.ts'
import { t } from '../lib/i18n.ts'
import type { Me } from '../lib/types.ts'
import { attentionCounters, plural, scoreTone, type Attention, type Counter } from './painel.tsx'

interface CondoOverview {
  slug: string
  summary: { today: string; score: number | null; overdue: number; dueSoon: number; attention: Attention } | null
}

const chip = 'inline-flex items-center gap-1.5 rounded-full border border-pauta bg-folha px-2.5 py-1 hover:bg-papel'

/**
 * Meus condomínios (ADR 0043): with two or more buildings, the start page says what needs attention in each, with
 * the painel's counters. Rows for roles that do not see the painel stay a plain link.
 */
export function MyCondos({ memberships }: { memberships: Me['memberships'] }) {
  const q = useQuery({ queryKey: ['overview'], queryFn: () => get<{ condos: CondoOverview[] }>('/api/overview'), staleTime: 30_000 })
  const bySlug = new Map((q.data?.condos ?? []).map((c) => [c.slug, c.summary]))
  return (
    <Sheet className="mt-6">
      {memberships.map((m) => {
        const s = bySlug.get(m.slug)
        const items: Counter[] = s
          ? [
              ...(s.overdue ? [{ to: '/c/$slug/prazos', icon: ClipboardCheck, n: s.overdue, text: plural(s.overdue, t('prazo vencido'), t('prazos vencidos')) }] : []),
              ...attentionCounters(s.attention).filter((k) => k.n > 0),
            ]
          : []
        const asm = s?.attention.nextAssembly
        return (
          <section key={m.slug} className="border-b border-pauta last:border-b-0" aria-label={m.name} data-testid={`condo-${m.slug}`}>
            <Link to="/c/$slug" params={{ slug: m.slug }} className="flex items-center justify-between gap-3 px-4 py-4 hover:bg-papel">
              <span className="font-semibold">{m.name}</span>
              <span className="text-sm text-tinta-2">{t(m.roleLabel)}</span>
            </Link>
            {s && (
              <div className="-mt-2 flex flex-wrap items-center gap-2 px-4 pb-4 text-sm">
                <Badge tone={scoreTone(s.score)}>{s.score === null ? t('sem prazos com data') : t('Prazos em dia: {score}%', { score: s.score })}</Badge>
                {items.map((k, i) => (
                  // two counters can point at the portaria, so the key is the position
                  <Link key={i} to={k.to} params={{ slug: m.slug }} className={chip}>
                    <k.icon className="size-4 text-caneta" />
                    <span>{k.text}{k.extra ? ` (${k.extra})` : ''}</span>
                  </Link>
                ))}
                {asm && (
                  <Link to="/c/$slug/assembleias/$aid" params={{ slug: m.slug, aid: asm.id }} className={chip}>
                    <Gavel className="size-4 text-caneta" />
                    <span>{t('Assembleia em {date}', { date: fmtDate(asm.at) })}</span>
                  </Link>
                )}
                {items.length === 0 && !asm && <span className="text-tinta-2">{t('Nada pendente hoje.')}</span>}
              </div>
            )}
          </section>
        )
      })}
    </Sheet>
  )
}
